import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

/* ------------------------------------------------------------------ */
/*  E2E / Playwright auth bootstrap.                                  */
/*  If an `e2e_token=<session_token>` query param is present on load  */
/*  (or `localStorage.e2e_token` is already set from a previous run), */
/*  attach `Authorization: Bearer <token>` to EVERY fetch that goes   */
/*  to the same-origin API. Cookies set via Playwright's              */
/*  `context.add_cookies` are not always attached to SPA XHRs in      */
/*  headless mode, so this Bearer path lets automated tests drive     */
/*  the app end-to-end without changing production auth behavior.     */
/*  In real user sessions no query param / localStorage key is set    */
/*  and the wrapper is a no-op.                                       */
/* ------------------------------------------------------------------ */
(function installE2EAuth() {
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("e2e_token");
    if (q) {
      window.localStorage.setItem("e2e_token", q);
      // Clean the URL so the token doesn't linger in history / logs.
      params.delete("e2e_token");
      const clean = window.location.pathname
        + (params.toString() ? "?" + params.toString() : "")
        + window.location.hash;
      window.history.replaceState({}, "", clean);
    }
  } catch (_) {}
  const token = (() => {
    try {
      return window.localStorage.getItem("e2e_token") || null;
    } catch (_) {
      return null;
    }
  })();
  if (!token) return;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    try {
      const url =
        typeof input === "string" ? input : input?.url || "";
      // Only add the header to same-origin API calls
      const same =
        url.startsWith("/api/") ||
        url.includes(String(process.env.REACT_APP_BACKEND_URL || ""));
      if (same) {
        const headers = new Headers(init.headers || {});
        if (!headers.has("Authorization")) {
          headers.set("Authorization", "Bearer " + token);
        }
        init = { ...init, headers };
      }
    } catch (_) {}
    return orig(input, init);
  };
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
